import Cookies from 'js-cookie';
import {
  APPLY_COUPON_PATH_PREFIX,
  PENDING_PROMO_CODE_COOKIE,
  PENDING_PROMO_CODE_TTL_DAYS,
} from 'helpers/constants/promoCode';

/**
 * Reads the coupon code out of a Contentful hyperlink target, or null if the link is an
 * ordinary one. Accepts an absolute path only — an author pasting a full URL to another
 * host must not turn that host's path into a coupon.
 */
export const parseApplyCouponLink = (uri?: string): string | null => {
  if (!uri?.startsWith(APPLY_COUPON_PATH_PREFIX)) return null;

  const rawCode = uri.slice(APPLY_COUPON_PATH_PREFIX.length).split(/[?#/]/)[0];

  // A code containing a bare `%` (`50%OFF`) or a truncated escape makes decodeURIComponent
  // throw. This runs inside the rich-text renderer, so an unhandled throw takes the whole
  // document down — treat it as an ordinary link instead.
  let decoded: string;
  try {
    decoded = decodeURIComponent(rawCode);
  } catch {
    return null;
  }

  return normalizePromoCode(decoded);
};

/**
 * Trims and sanity-checks a code, preserving case exactly. commercetools looks discount
 * codes up case-sensitively and this project's codes are mixed-case (`craig-test`,
 * `BrettTest`), so normalizing case here would make them fail to redeem. Returns null for
 * anything that isn't a plausible code so a malformed link renders as inert text rather
 * than a control that always fails.
 */
export const normalizePromoCode = (code?: string): string | null => {
  const trimmed = code?.trim() ?? '';

  return /^[A-Za-z0-9._-]{1,64}$/.test(trimmed) ? trimmed : null;
};

export const getPendingPromoCode = (): string | null => normalizePromoCode(Cookies.get(PENDING_PROMO_CODE_COOKIE));

export const setPendingPromoCode = (code: string) => {
  Cookies.set(PENDING_PROMO_CODE_COOKIE, code, {
    expires: PENDING_PROMO_CODE_TTL_DAYS,
    path: '/',
    // Lax, not Strict: the shopper may arrive at the page holding the link via a top-level
    // navigation from an email client or ad, which Strict would drop.
    sameSite: 'lax',
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
};

/**
 * Holds a code without touching an already-identical stored value. `setPendingPromoCode`
 * re-stamps the 30-day expiry, so a background retry firing on every page load would renew
 * the cookie indefinitely and a code from a finished promotion would never age out.
 */
export const holdPendingPromoCode = (code: string) => {
  if (getPendingPromoCode() === code) return;

  setPendingPromoCode(code);
};

export const clearPendingPromoCode = () => {
  Cookies.remove(PENDING_PROMO_CODE_COOKIE, { path: '/' });
};
